import React, { useEffect, useState } from 'react';
import { Button, Switch } from '@insly/qmt-reactjs-ui-lib';
import axios, { AxiosError } from 'axios';
import { TError, TFetchStatus } from 'models';
import { TInsurer } from 'app/Pages/User/profile/body/Insurers';
import { API_GET_ALL_ORDERED_INSURERS } from 'app/Components/common/InsurersList';
import { IBrokerProps } from 'app/Pages/common';
import { cloneObject, errorNotification, preflightAPICall, processAPIError, translate } from 'utils';
import { useApp } from 'context/App';
import LoadingOverlay from 'helpers/LoadingOverlay';
import { renderErrors } from 'app/Components/common/Error';
import {
  CepikSettingsStyled,
  CepikInsurerRow,
  CepikInsurerHeader,
  CepikInsurerSide,
  CepikInsurerTitle,
  CepikToggleBody,
  CepikToggleLabel,
  CepikControlsStyled,
  CepikInfoBlock,
} from './styles';

interface ICepikSettings {
  onSubmit: (props: IBrokerProps, onError: (errors: AxiosError) => void, onSuccess: () => void) => void,
  props?: IBrokerProps,
}

const CepikSettings = ({ onSubmit, props }: ICepikSettings) => {
  const { showNotification } = useApp();

  const [fetchStatus, setFetchStatus] = useState<TFetchStatus>(null);
  const [errors, setErrors] = useState<TError[]>([]);
  const [insurersList, setInsurersList] = useState<TInsurer[]>([]);
  const [cepikInsurers, setCepikInsurers] = useState<string[]>(props?.cepik?.insurers ?? []);

  useEffect(() => {
    getAllInsurers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAllInsurers = () => {
    setFetchStatus('loading');
    preflightAPICall(() => {
      axios.get(API_GET_ALL_ORDERED_INSURERS)
        .then(response => {
          setInsurersList(response.data.results);
          setFetchStatus('success');
        }).catch(handleFetchError);
    });
  };

  const handleFetchError = (error: AxiosError) => {
    setErrors(processAPIError(error, true) as TError[]);
    setFetchStatus('failed');
  };

  const onError = (error: AxiosError) => {
    setFetchStatus('failed');
    errorNotification(error, showNotification);
  };

  const onSuccess = () => setFetchStatus('success');

  const handleToggle = (tag: string, enabled: boolean) => {
    if (enabled) {
      setCepikInsurers(prev => [...prev, tag]);
    } else {
      setCepikInsurers(prev => prev.filter(t => t !== tag));
    }
  };

  const handleSubmit = () => {
    setFetchStatus('loading');
    const _props = props ? cloneObject(props) : {};
    _props.cepik = {
      ..._props.cepik,
      insurers: cepikInsurers.length ? cepikInsurers : null,
    };
    onSubmit(_props, onError, onSuccess);
  };

  if (fetchStatus === 'loading' && !insurersList.length) {
    return <LoadingOverlay />;
  }

  if (fetchStatus === 'failed' && !insurersList.length) {
    return <>{renderErrors(errors)}</>;
  }

  const saveButton = (
    <CepikControlsStyled>
      <Button size="l" onClick={handleSubmit}>{translate({ key: 'common.button.save' })}</Button>
    </CepikControlsStyled>
  );

  return (
    <>
      {fetchStatus === 'loading' ? <LoadingOverlay /> : null}
      <CepikSettingsStyled>
        <CepikInfoBlock>{translate({ key: 'master.broker.cepik.insurers_info' })}</CepikInfoBlock>
        {saveButton}
        {insurersList.map(insurer => (
          <CepikInsurerRow key={insurer.id}>
            <CepikInsurerHeader>
              <CepikInsurerSide>
                <CepikInsurerTitle>{insurer.title}</CepikInsurerTitle>
              </CepikInsurerSide>
              <CepikToggleBody>
                <CepikToggleLabel>{translate({ key: 'master.broker.cepik.enabled' })}</CepikToggleLabel>
                <Switch
                  name={insurer.tag}
                  checked={cepikInsurers.includes(insurer.tag)}
                  handleChange={(name, value) => handleToggle(name as string, value as boolean)}
                />
              </CepikToggleBody>
            </CepikInsurerHeader>
          </CepikInsurerRow>
        ))}
        {saveButton}
      </CepikSettingsStyled>
    </>
  );
};

export default CepikSettings;
